import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useMediaSrc } from '../useMediaSrc'
import { fanLabel, type Fan } from './collectionsData'
import type { FanCardState } from './useFanJob'

interface Props {
  fan: Fan
  selected?: boolean
  /** Shown only to people who may change lists. */
  onToggle?: () => void
  onOpen: (e: React.MouseEvent) => void
  onMessage: () => void
  job?: FanCardState
}

export function lastSeenText(iso: string | null, lang: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(lang, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** One fan in a list (design fan card): banner, avatar, names, last seen, Message. */
export function FanCard({ fan, selected = false, onToggle, onOpen, onMessage, job }: Props) {
  const { t, i18n } = useTranslation('library')
  const banner = useMediaSrc(fan.header)
  const avatar = useMediaSrc(fan.avatar)
  const label = fanLabel(fan)
  const seen = lastSeenText(fan.lastSeen, i18n.language)
  return (
    <div
      data-k={fan.id}
      className={`lib-fan ${selected ? 'is-on' : ''} ${job ? `is-${job.state}` : ''}`}
      role="option"
      aria-selected={selected}
      aria-label={label}
      onClick={onOpen}
    >
      <div ref={banner.ref as React.RefObject<HTMLDivElement>} className="lib-fan-banner">
        {banner.src && <img src={banner.src} alt="" loading="lazy" />}
      </div>
      {onToggle && (
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={t('select')}
          className="lib-check"
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
        >
          <span className="lib-check-dot">{selected && '✓'}</span>
        </button>
      )}
      <div className="lib-fan-body">
        <span ref={avatar.ref as React.RefObject<HTMLSpanElement>} className="lib-fan-avatar">
          {avatar.src ? <img src={avatar.src} alt="" loading="lazy" /> : label.slice(0, 1).toUpperCase()}
        </span>
        <span className="lib-fan-name">
          <span className="lib-fan-label">{label}</span>
          {fan.verified && <FeatherIcon name="check-circle" size={13} />}
        </span>
        <span className="lib-fan-handle">@{fan.username}</span>
        <span className="lib-fan-seen">{seen && t('collections.lastSeen', { when: seen })}</span>
        {job?.state === 'failed' && <span className="lib-fan-error">{job.error}</span>}
        <button
          type="button"
          className="lib-btn lib-fan-message"
          onClick={(e) => {
            e.stopPropagation()
            onMessage()
          }}
        >
          <FeatherIcon name="message-square" size={13} />
          {t('collections.message')}
        </button>
      </div>
      {job?.state === 'running' && <span className="om-spin lib-fan-spinner" role="status" aria-label={t('collections.working')} />}
      {job?.state === 'done' && <span className="lib-fan-done" aria-hidden="true">✓</span>}
    </div>
  )
}
